import { TurnstileWidget, isTurnstileEnabled } from '@/components/auth/TurnstileWidget';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { api, extractApiError } from '@/lib/api';
import { trackAnon } from '@/lib/telemetry';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

const signUpSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(12, 'At least 12 characters'),
  display_name: z
    .string()
    .max(200, 'Maximum 200 characters')
    .optional()
    .transform((v) => (v && v.length > 0 ? v : undefined)),
});

type SignUpValues = z.infer<typeof signUpSchema>;

export default function SignUp() {
  const navigate = useNavigate();
  // No local auth-guard — PublicLayout redirects authed users to /dashboard.

  // Turnstile token state. Only required when the widget is enabled at
  // build time. Cloudflare tokens are single-use server-side, so we clear
  // the local state after every submit attempt to force a fresh challenge.
  const turnstileEnabled = isTurnstileEnabled();
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  // signup.started — fires once when the user first focuses the form.
  // Ref-gated so a tab in/out doesn't double-count for the same visit.
  const signupStartedFiredRef = useRef(false);
  const fireSignupStarted = () => {
    if (signupStartedFiredRef.current) return;
    signupStartedFiredRef.current = true;
    trackAnon('signup.started');
  };

  const form = useForm<SignUpValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { email: '', password: '', display_name: '' },
  });

  const mutation = useMutation({
    mutationFn: async (values: SignUpValues) => {
      const payload: SignUpValues & { turnstile_token?: string } = { ...values };
      if (turnstileToken) payload.turnstile_token = turnstileToken;
      const resp = await api.post('/auth/signup', payload);
      return resp.data;
    },
    onSuccess: () => {
      toast.success('Account created. Check your email to verify, then sign in.');
      navigate('/sign-in', { replace: true });
    },
    onError: (err) => {
      // Tokens are single-use — invalidate ours so the user gets a fresh
      // challenge on retry. The widget's expired/error callbacks handle the
      // visual reset; we just clear the form-level state.
      setTurnstileToken(null);
      const apiErr = extractApiError(err);
      if (apiErr?.code === 'auth.email_taken') {
        form.setError('email', { message: 'This email is already registered' });
        return;
      }
      if (apiErr?.code === 'auth.signup_invalid_email') {
        form.setError('email', { message: apiErr.message || 'Invalid email' });
        return;
      }
      if (apiErr?.code === 'auth.password_policy') {
        const violations = (apiErr.metadata?.violations as string[] | undefined) ?? [];
        const message = violations.length > 0 ? violations.join('. ') : apiErr.message;
        form.setError('password', { message });
        return;
      }
      if (apiErr?.code === 'auth.bot_check_failed') {
        toast.error('Bot protection check failed. Please refresh and try again.');
        return;
      }
      if (apiErr?.code === 'auth.rate_limited') {
        toast.error('Too many attempts. Please try again later.');
        return;
      }
      toast.error(apiErr?.message || 'Sign-up failed. Please try again.');
    },
  });

  const onSubmit = (values: SignUpValues) => {
    mutation.mutate(values);
  };

  // react-hook-form swallows invalid submits silently — show a toast so users
  // notice the per-field FormMessage instead of thinking the button is broken.
  const onInvalid = (errors: Record<string, { message?: string }>) => {
    const first = Object.values(errors).find((e) => e?.message);
    toast.error(first?.message || 'Please fix the highlighted fields.');
  };

  const isPending = mutation.isPending;
  // When Turnstile is wired but the user hasn't completed the challenge yet,
  // disable the submit so we don't waste a round-trip on a guaranteed 400.
  const turnstileBlocked = turnstileEnabled && !turnstileToken;
  const submitDisabled = isPending || turnstileBlocked;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create your account</CardTitle>
        <CardDescription>Start running causal discoveries in minutes.</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} onFocus={fireSignupStarted}>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="display_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name (optional)</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      autoComplete="name"
                      placeholder="Ada Lovelace"
                      disabled={isPending}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      placeholder="At least 12 characters"
                      disabled={isPending}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {turnstileEnabled && (
              <div className="flex flex-col gap-1">
                <TurnstileWidget
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                />
                {turnstileBlocked && (
                  <p className="text-xs text-muted-foreground">
                    Please complete the bot check above.
                  </p>
                )}
              </div>
            )}
          </CardContent>
          <CardFooter className="flex flex-col gap-3">
            <Button type="submit" className="w-full" disabled={submitDisabled}>
              {isPending ? 'Creating account…' : 'Sign up'}
            </Button>
            <div className="w-full text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/sign-in" className="text-foreground hover:underline">
                Sign in
              </Link>
            </div>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
}
